import type { SxProps, Theme } from '@mui/material';
import { Box, IconButton, Typography } from 'hephaestus/web';
import {
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
} from 'hephaestus/webIcons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Dialog, DialogContent, DialogTitle } from '../dialog/dialog';

type InfoDialogProps = {
  title?: string;
  information: string;
  center?: boolean;
  sx?: SxProps<Theme>;
  dialogSx?: SxProps<Theme>;
};

const InfoDialog = ({
  title,
  information,
  center,
  sx,
  dialogSx,
}: InfoDialogProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation(['components']);

  const handleIconClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
  };

  const handleOnClose = (
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
  };

  return (
    <>
      <span
        style={{
          verticalAlign: 'middle',
          ...(center ? { display: 'flex', alignItems: 'center' } : {}),
        }}
      >
        <InfoOutlinedIcon
          data-testid='info-icon'
          sx={[
            {
              fontSize: '14px',
              cursor: 'pointer',
              marginLeft: '6px',
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ]}
          onClick={handleIconClick}
        />
      </span>
      <Dialog
        open={open}
        onClose={handleOnClose}
        maxWidth='sm'
        fullWidth
        sx={dialogSx}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Typography variant='headingSmall' sx={{ mr: 0.5 }}>
              {title ?? t('components:additionalInfoModal.title')}
            </Typography>
            <IconButton
              onClick={handleOnClose}
              aria-label='close'
              size='medium'
            >
              <CloseIcon fontSize='inherit' />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant='bodyLarge' sx={{ table: { width: '100%' } }}>
            <ReactMarkdown
              className='markdown'
              remarkPlugins={[remarkGfm]}
              linkTarget={'_blank'}
            >
              {information}
            </ReactMarkdown>
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InfoDialog;
